<!--
  Số liệu sẽ chốt của một cửa hàng / bộ phận: dải tổng + bảng từng nhân viên.
  Dùng chung cho phần xem trước khi chốt một cửa hàng và modal xem chi tiết ở bảng chốt
  hàng loạt, để hai chỗ luôn hiện cùng một kiểu.
-->
<template>
  <div>
    <div class="preview-summary mb-3">
      <div
        v-for="part in previewParts"
        :key="part.key"
        class="preview-summary__part"
        :class="{ 'preview-summary__part--total': part.strong }"
      >
        <div class="preview-summary__label">{{ part.label }}</div>
        <div class="preview-summary__value" :class="amountClass(part.raw)">
          {{ part.value }}
        </div>
      </div>
    </div>
    <b-table
      class="table-bordered table-hover"
      :fields="previewFields"
      :items="previewItems"
      small
      show-empty
      empty-text="Kỳ này không có nhân sự nào phát sinh thưởng"
    >
      <template v-slot:cell(employeeName)="row">
        <span class="font-weight-bold">{{ row.item.employeeName }}</span>
        <span class="text-muted">
          ({{ row.item.employeeCode }}) · {{ row.item.jobTitleName }}</span
        >
      </template>
      <template v-slot:cell(commissionAmount)="row">
        {{ displayAmount(row.item.commissionAmount) }}
      </template>
      <template v-slot:cell(adhocAmount)="row">
        {{ displayAmount(row.item.adhocAmount) }}
      </template>
      <template v-slot:cell(bonusAmount)="row">
        {{ displayAmount(row.item.bonusAmount) }}
      </template>
      <template v-slot:cell(minusAmount)="row">
        {{ displayAmount(row.item.minusAmount) }}
      </template>
      <template v-slot:cell(totalAmount)="row">
        <span
          class="font-weight-bold"
          :class="amountClass(row.item.totalAmount)"
          >{{ displayAmount(row.item.totalAmount) }}</span
        >
      </template>
    </b-table>
    <div v-if="previewZeroCount" class="text-muted small">
      {{ previewZeroCount }} nhân sự có tổng thưởng bằng 0 không hiện ở bảng
      trên nhưng vẫn được lưu khi chốt.
    </div>
  </div>
</template>

<script>
import commissionReportMixin, { thStyle } from './commissionReport.mixin';

export default {
  mixins: [commissionReportMixin],
  props: {
    /** { items: dòng tổng hợp theo nhân viên, totals: tổng của kỳ } — từ GET commission-reports */
    preview: { type: Object, required: true },
  },
  data() {
    return {
      previewFields: [
        { key: 'employeeName', label: 'Nhân viên', thStyle: thStyle('34%') },
        {
          key: 'commissionAmount',
          label: 'Hoa hồng',
          thStyle: thStyle('14%'),
          tdClass: 'text-right',
        },
        {
          key: 'adhocAmount',
          label: 'AdHoc',
          thStyle: thStyle('12%'),
          tdClass: 'text-right',
        },
        {
          key: 'bonusAmount',
          label: 'Cộng khác',
          thStyle: thStyle('12%'),
          tdClass: 'text-right',
        },
        {
          key: 'minusAmount',
          label: 'Trừ khác',
          thStyle: thStyle('12%'),
          tdClass: 'text-right',
        },
        {
          key: 'totalAmount',
          label: 'Tổng thưởng',
          thStyle: thStyle('16%'),
          tdClass: 'text-right',
        },
      ],
    };
  },
  computed: {
    /** Bảng xem trước chỉ hiện dòng có tiền; dòng 0 vẫn được lưu khi chốt */
    previewItems() {
      return this.preview
        ? this.preview.items.filter((row) => Number(row.totalAmount) !== 0)
        : [];
    },
    previewZeroCount() {
      return this.preview
        ? this.preview.items.length - this.previewItems.length
        : 0;
    },
    previewParts() {
      const totals = (this.preview && this.preview.totals) || {};
      return [
        {
          key: 'employeeCount',
          label: 'Nhân sự',
          value: totals.employeeCount || 0,
        },
        { key: 'commissionAmount', label: 'Hoa hồng', money: true },
        { key: 'adhocAmount', label: 'AdHoc', money: true },
        { key: 'bonusAmount', label: 'Cộng khác', money: true },
        { key: 'minusAmount', label: 'Trừ khác', money: true },
        { key: 'totalAmount', label: 'Tổng thưởng', money: true, strong: true },
      ].map((part) =>
        part.money
          ? {
              ...part,
              raw: totals[part.key],
              value: this.displayAmount(totals[part.key]),
            }
          : { ...part, raw: 0 },
      );
    },
  },
};
</script>

<style scoped>
.preview-summary {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  border: 1px solid #ebedf3;
  border-radius: 6px;
  overflow: hidden;
}
.preview-summary__part {
  padding: 10px 14px;
  border-right: 1px solid #ebedf3;
}
.preview-summary__part:last-child {
  border-right: 0;
}
.preview-summary__part--total {
  background: #f1faff;
}
.preview-summary__label {
  font-size: 12px;
  color: #7e8299;
  margin-bottom: 2px;
}
.preview-summary__value {
  font-size: 15px;
  font-weight: 600;
  color: #181c32;
}
</style>
